import { z } from 'zod';

import { isDeliverableTemplateKey } from '../../notifications/notification-catalogue/notification-catalogue';
import { NotificationTemplateKeySchema } from '../../notifications/notification-template-key/notification-template-key';
import { needsSuspensionDays } from '../violation-action/violation-action';
import { ViolationActionSchema } from '../violation-action/violation-action';
import { ViolationSeveritySchema } from '../violation-severity/violation-severity';
import {
  CLASSIFIER_GUIDANCE_MAX_LENGTH,
  ViolationTypeKeySchema,
  ViolationTypeTranslationsSchema,
} from '../violation-type/violation-type';

/**
 * The editable half of a violation type, before the duration rule is applied.
 *
 * Split out from the refined schema below because **Zod's `.refine` returns an
 * effect, and an effect cannot be `.extend`ed** — the update schema needs to
 * `.partial()` these fields, which is impossible once the rule is attached. So
 * the shape is declared once here and each request schema attaches
 * {@link assertSuspensionDaysMatchAction} for itself.
 */
export const ViolationTypeWritableShape = {
  severity: ViolationSeveritySchema,
  action: ViolationActionSchema,
  suspensionDays: z.number().int().min(1).max(3650).nullable(),
  reviewBeforeEnforce: z.boolean(),
  /**
   * The notification template rendered as this type's own notice (N17).
   *
   * **It must be a key the platform can actually deliver** — checked against the
   * notification catalogue rather than accepted as free text. Until P21a there
   * was nothing to check it against, so a typo here was a notice nobody ever
   * got, with nothing anywhere to say so (PROGRESS §Known issues, opened by
   * P20b). `moderation.violation.<type_key>` resolves through the catalogue's
   * family fallback, so the six seeded types and every runtime type an operator
   * creates are deliverable whether or not anybody has authored bespoke copy for
   * them.
   *
   * The admin surface offers this as a **picker** (P21b) reading
   * `GET /admin/notification-templates/catalogue`; this refusal is what makes
   * the picker a guarantee rather than a convenience.
   */
  legalNoticeTemplateKey: NotificationTemplateKeySchema.refine(isDeliverableTemplateKey, {
    message: 'That is not a notification this platform can send.',
  }),
  classifierGuidance: z.string().trim().min(1).max(CLASSIFIER_GUIDANCE_MAX_LENGTH),
  isActive: z.boolean(),
  translations: ViolationTypeTranslationsSchema,
};

/**
 * The rule that ties the duration to the action, in **one** place.
 *
 * A `suspend` with no `suspensionDays` is an indefinite suspension: the elapse
 * sweep looks for a `suspended_until` in the past and would never find one, so
 * the member is banned by an omission nobody chose. A duration on a `warn` is
 * the opposite mistake — a number that reads as a consequence and does nothing.
 *
 * Both are refused at the contract rather than discovered by the offender.
 */
export function assertSuspensionDaysMatchAction(
  value: { action?: unknown; suspensionDays?: number | null },
  ctx: z.RefinementCtx,
): void {
  // A partial update need not name the action; the service re-checks the merged
  // row, which is the only place both halves are known.
  if (value.action === undefined) {
    return;
  }

  const parsed = ViolationActionSchema.safeParse(value.action);
  if (!parsed.success) {
    return;
  }

  const required = needsSuspensionDays(parsed.data);
  const given = value.suspensionDays ?? null;

  if (required && given === null) {
    ctx.addIssue({
      code: 'custom',
      path: ['suspensionDays'],
      message: 'A suspend action needs a suspension length in days.',
    });
  }

  if (!required && given !== null) {
    ctx.addIssue({
      code: 'custom',
      path: ['suspensionDays'],
      message: 'Only a suspend action carries a suspension length.',
    });
  }
}

/**
 * Creating a violation type (`violations.manage`).
 *
 * The `key` is settable here and **nowhere else** — see
 * `ViolationTypeUpdateSchema`, which omits it on purpose.
 */
export const ViolationTypeCreateSchema = z
  .object({
    key: ViolationTypeKeySchema,
    ...ViolationTypeWritableShape,
  })
  .strict()
  .superRefine(assertSuspensionDaysMatchAction);

export type ViolationTypeCreateRequest = z.infer<typeof ViolationTypeCreateSchema>;
